import { StrictMode, useState, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import './index.css';
import { router } from './routes/router.js';
import { useAuth } from './stores/auth.js';
import { loadRemoteConfig } from './lib/remoteConfig.js';
import { flushOutbox } from './session/finalize.js';
import { purgeExpiredFrames } from './session/db.js';
import { daysBucket, setAnalyticsConsent, storedAnalyticsConsent, track, trackScreen } from './lib/analytics.js';
import { readEnv } from './lib/env.js';
import { registerServiceWorker } from './lib/pwa.js';
import { listenForReminderTaps } from './lib/reminders.js';
import { listenForAppLinks } from './lib/appLinks.js';
import { setStatusBarBackdrop } from './lib/systemBars.js';
import { Splash } from './features/splash/Splash.js';
import { isLandingPath } from './features/landing/content.js';
import { installErrorReporting } from './lib/crashlytics.js';
import { installMotionPermission } from './lib/motion.js';

installErrorReporting();
installMotionPermission();

/**
 * Boot order matters:
 *  1. analytics consent (nothing is sent before the user allows it)
 *  2. auth listener, so the router knows who is here
 *  3. Remote Config, which the capture screen needs
 *  4. housekeeping: retry offline writes, purge expired local frames
 */
const container0 = document.getElementById('root');

/**
 * A fresh clone has no Firebase config. Say so in the page instead of rendering
 * a blank screen — this is the first thing a new contributor sees.
 */
function showBootError(error: unknown): void {
  // Native keeps its launch screen up until the JS splash takes over; a boot
  // failure must not leave the user staring at it.
  void import('@capacitor/splash-screen')
    .then(({ SplashScreen }) => SplashScreen.hide())
    .catch(() => undefined);
  if (!container0) return;
  container0.innerHTML = `
    <main style="max-width:38rem;margin:4rem auto;padding:0 1.5rem;font-family:system-ui">
      <h1 style="font-size:1.5rem">Erectus could not start</h1>
      <p style="color:#475569;line-height:1.6">${escapeHtml(String((error as Error)?.message ?? error))}</p>
      <pre style="background:#f1f5f9;padding:1rem;border-radius:.75rem;overflow:auto">cp .env.example apps/web/.env.local</pre>
      <p style="color:#64748b;font-size:.9rem">
        With <code>VITE_USE_EMULATORS=1</code> the app runs entirely against the local emulator suite.
      </p>
    </main>`;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );
}

try {
  readEnv();
} catch (error) {
  showBootError(error);
  throw error;
}

setAnalyticsConsent(storedAnalyticsConsent());

try {
  // Dark text on the splash's light ground; the splash hands back per screen.
  void setStatusBarBackdrop('light');
  useAuth.getState().init();
  // One screen_view per route (the native SDK only sees one view controller).
  trackScreen(router.state.location.pathname);
  router.subscribe((state) => trackScreen(state.location.pathname));
  void loadRemoteConfig();
  // Retry queued uploads first: a session waiting on its keypoint upload keeps its frames.
  void flushOutbox()
    .catch(() => 0)
    .then(() => purgeExpiredFrames());
  // Replays live in IndexedDB for a week; ask the browser not to evict it under
  // storage pressure in the meantime. Native apps keep it anyway; denial is fine.
  void navigator.storage?.persist?.().catch(() => false);
  void registerServiceWorker();
  void listenForReminderTaps((link) => {
    const last = useAuth.getState().userDoc?.gamification?.lastSessionAt ?? null;
    track('reminder_open', {
      days_since_last_session_bucket: daysBucket(last === null ? Infinity : (Date.now() - last) / 86_400_000),
    });
    void router.navigate(link);
  });
  void listenForAppLinks({
    onEmailSignIn: (link) => useAuth.getState().completeEmailLink(link),
    onPath: (path) => void router.navigate(path, { replace: true }),
  });
} catch (error) {
  showBootError(error);
  throw error;
}

window.addEventListener('online', () => {
  void flushOutbox();
});

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

/**
 * The splash plays once per launch, over the router — the first screen
 * renders and fills underneath it. Policy pages are opened from links (store
 * listings, the landing page) and go straight to the text; the landing page
 * has its own intro.
 */
function App(): ReactNode {
  const [splash, setSplash] = useState(
    () => !window.location.pathname.startsWith('/legal/') && !isLandingPath(window.location.pathname),
  );
  return (
    <>
      <RouterProvider router={router} />
      {splash ? (
        <Splash
          onDone={() => {
            setSplash(false);
            const path = router.state.location.pathname;
            const camera = path.startsWith('/capture') || path.startsWith('/session');
            void setStatusBarBackdrop(camera ? 'camera' : 'light');
          }}
        />
      ) : null}
    </>
  );
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
